import React, { useState } from 'react';
import {
  formatCurrency,
  validatePaymentAmount,
  PAYMENT_TYPES,
  PAYMENT_TYPE_LABELS,
  buildServicesToBook,
  effectiveUnitPriceAfterCartDiscount,
  subtotalAfterItemDiscounts,
} from '../utils/checkoutCalculations';
import { bookingService } from '../../../_services/booking.service';
import { paymentActions } from '../../../_actions';
import toast from '../../shared/Toast';

// Icons
const CashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const CardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);
const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const ReaderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="18" rx="3" /><line x1="8" y1="15" x2="16" y2="15" /><circle cx="12" cy="10" r="2" />
  </svg>
);
const TapToPayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="2" width="12" height="20" rx="2" /><circle cx="12" cy="18" r="0.5" fill="currentColor" />
    <path d="M2 8a3 3 0 0 1 0 4" /><path d="M4 6a5 5 0 0 1 0 8" />
  </svg>
);
const OtherIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

// Deposit-friendly payment methods (no Klarna/Clearpay)
const DEPOSIT_METHODS = [
  { type: PAYMENT_TYPES.CARD_READER, key: 'beuCardReaders', label: 'Card Reader', icon: ReaderIcon, color: '#29282D' },
  { type: PAYMENT_TYPES.TAP_TO_PAY, key: 'tapToPay', label: 'Tap to Pay', icon: TapToPayIcon, color: '#000000', requiresTapToPay: true },
  { type: PAYMENT_TYPES.PAYMENT_LINK, key: 'paymentLink', label: 'Payment Link', icon: LinkIcon, color: '#007EA0' },
  { type: PAYMENT_TYPES.CASH, key: 'cash', label: 'Cash', icon: CashIcon, color: '#34C759' },
  { type: PAYMENT_TYPES.CARD, key: 'card', label: 'Card', icon: CardIcon, color: '#3b82f6' },
  { type: PAYMENT_TYPES.OTHER, key: 'other', label: 'Other', icon: OtherIcon, color: '#8e8e93' },
];

/**
 * DepositScreen
 * Take a deposit payment without completing the booking.
 * Custom amount + payment method selection.
 * Saves to booking with bookingStatus: 0 (still open).
 */
const DepositScreen = ({ state, actions, onBack, onDepositComplete, currencySymbol, dispatch, terminal, paymentMethods, tapToPay }) => {
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fmt = (a) => formatCurrency(a, currencySymbol);

  const enabledMethods = DEPOSIT_METHODS.filter(method => {
    const methods = state.paymentMethods;
    if (!methods || Object.keys(methods).length === 0) {
      if (method.requiresTapToPay) return false;
      return true;
    }
    // Tap to Pay also gated on device support
    if (method.requiresTapToPay && !tapToPay?.isAvailable) return false;
    return methods[method.key] !== false;
  });

  const handleSelectMethod = async (method) => {
    const parsed = parseFloat(amount);
    const validation = validatePaymentAmount(parsed, state.balanceRemaining);
    if (!validation.valid) {
      setAmountError(validation.error);
      return;
    }
    setAmountError(null);
    setSaving(true);

    try {
      // For simple methods (cash, card, other), record the deposit directly
      if (method.type === PAYMENT_TYPES.CASH || method.type === PAYMENT_TYPES.CARD || method.type === PAYMENT_TYPES.OTHER) {
        const salonId = state.salonId || localStorage.getItem('salonId');
        const hairdresserId = state.booking?.hairdresserId
          || state.booking?.hairdresserInfo?._id
          || state.lineItems[0]?.staffId
          || localStorage.getItem('userId');
        const clientId = state.client?._id || state.client?.userId || null;

        // Build services from current line items for V2 API.
        // Use cart-discount-aware unit price so the booking record reflects
        // the actual sold-at price downstream (commission calc + cashup).
        const denomDep = subtotalAfterItemDiscounts(state.lineItems);
        const servicesBooked = state.lineItems
          .filter(item => item.type === 'service')
          .map(item => ({
            serviceId: item.serviceId || item.id,
            servicePrice: effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denomDep),
            serviceTime: item.serviceTime || 0,
            depositPrice: item.depositPrice || 0,
          }));

        // Save deposit via V2 checkout-edit (keeps booking open with bookingStatus: 0).
        // For off-platform deposits (cash/card/other) we ONLY push the transaction
        // row. Don't set isDeposit/isDepositPay/depositAmount — those trigger the
        // online-deposit commission/fee path which would double-subtract the £ at
        // final checkout (the transaction row is already factored into _cashPayment).
        // See DEPOSIT-HANDLING-PLAN.md §5.4.
        await bookingService.editCheckoutBooking({
          bookingId: state.bookingId,
          hairdresserId,
          salonId,
          userId: clientId || hairdresserId,
          servicesBooked,
          products: [],
          vouchers: [],
          transaction: JSON.stringify([
            // Preserve any deposits / payments already on the booking so this
            // save doesn't clobber them. The new BE serializer keeps these
            // tagged rows intact (including createdAt, so timestamps don't
            // collapse onto the final-checkout time).
            ...state.existingTransactions.map((t) => ({
              paymentType: t.paymentType,
              amount: t.amount,
              isDeposit: !!t.isDeposit,
              pgObjectType: t.isDeposit ? 'deposit' : 'charge',
              ...(t.createdAt ? { createdAt: t.createdAt } : {}),
            })),
            {
              paymentType: method.type,
              amount: validation.amount,
              isDeposit: true,
              pgObjectType: 'deposit',
              // Stamp now — when the deposit was actually taken, not when
              // the booking eventually completes.
              createdAt: new Date().toISOString(),
            },
          ]),
          totalAmount: parseFloat(state.totalDue).toFixed(2),
          isCheckout: 0,
          selectedPaymentType: method.type,
        });

        // Push the deposit into local state so it survives the next
        // editCheckoutBooking save (CheckoutPanel merges existingTransactions
        // into the persisted transaction[]). Otherwise the final-checkout save
        // would overwrite booking.transaction[] with only the new session
        // transactions and clobber the deposit.
        if (actions.addExistingTransaction) {
          actions.addExistingTransaction({
            paymentType: method.type,
            amount: validation.amount,
            label: 'Deposit',
            isDeposit: true,
            createdAt: new Date().toISOString(),
          });
        }

        toast.success(`${fmt(validation.amount)} deposit recorded`);
        if (onDepositComplete) onDepositComplete();
      } else if (method.type === PAYMENT_TYPES.CARD_READER) {
        // Card reader deposits need the full reader picker / connection flow,
        // which lives in CardReaderScreen. Stash the deposit amount in state
        // and navigate there — CheckoutPanel's cardReader case detects
        // pendingDepositAmount and runs the deposit save instead of the
        // regular checkout flow.
        if (actions.setPendingDeposit) {
          actions.setPendingDeposit(validation.amount);
        }
        actions.setScreen('cardReader');
        setSaving(false);
        return;
      } else if (method.type === PAYMENT_TYPES.TAP_TO_PAY) {
        // Tap to Pay deposits go through the same pendingDeposit pattern as
        // Card Reader, but route to the dedicated tapToPay screen which
        // wraps the native iOS Stripe Terminal SDK.
        if (actions.setPendingDeposit) {
          actions.setPendingDeposit(validation.amount);
        }
        actions.setScreen('tapToPay');
        setSaving(false);
        return;
      } else if (method.type === PAYMENT_TYPES.PAYMENT_LINK && dispatch && paymentMethods) {
        // Payment link deposit — create link, then redirect to payment link screen
        const servicesToBook = buildServicesToBook(state.lineItems, state.cartDiscount);
        const response = await dispatch(paymentActions.createPaymentLink({
          bookingId: state.bookingId,
          servicesToBook,
          productIds: [],
          voucherIds: [],
          amount: validation.amount,
          isDeposit: true,
        }));

        if (response?.data?.statusCode === 1) {
          // Show the link so the salon can copy/share it. Flagged as a deposit
          // so when paid it records a deposit and keeps the booking open — a
          // deposit link collects a deposit, it does not complete the booking.
          paymentMethods.showDepositPaymentLink(response.data.responseData, validation.amount);
        } else {
          toast.error('Failed to create payment link');
          setSaving(false);
        }
      } else {
        toast.error('This payment method is not available');
        setSaving(false);
        return;
      }
    } catch (e) {
      toast.error('Failed to save deposit. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="co-discount">
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onBack} disabled={saving}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">Take Deposit</h2>
      </div>

      <div className="co-discount-body">
        {/* Amount input */}
        <div className="co-deposit-amount-wrap">
          <span className="co-deposit-currency">{currencySymbol}</span>
          <input
            type="number"
            className="co-deposit-amount-input"
            placeholder="0.00"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={e => { setAmount(e.target.value); setAmountError(null); }}
            autoFocus
          />
        </div>
        {amountError && <p className="co-error-msg" style={{ marginTop: 6 }}>{amountError}</p>}
        <p style={{ fontSize: 13, color: '#8e8e93', marginTop: 10, textAlign: 'center' }}>
          Balance: {fmt(state.balanceRemaining)}
        </p>

        {/* Payment methods */}
        <div className="co-section-title" style={{ marginTop: 20 }}>Payment Method</div>
        <div className="co-payment-grid">
          {enabledMethods.map(method => {
            const Icon = method.icon;
            return (
              <button
                key={method.type}
                className="co-payment-tile"
                onClick={() => handleSelectMethod(method)}
                disabled={saving || !amount || parseFloat(amount) <= 0}
              >
                <div className="co-payment-tile-icon" style={{ backgroundColor: method.color }}>
                  <Icon />
                </div>
                <span className="co-payment-tile-label">{method.label}</span>
              </button>
            );
          })}
        </div>

        {saving && (
          <div className="co-loading" style={{ padding: '20px 0' }}>
            <div className="co-spinner" />
          </div>
        )}
      </div>

      <div className="co-pl-footer">
        <button className="bdp-btn bdp-btn--secondary" onClick={onBack} disabled={saving} style={{ flex: 1 }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DepositScreen;
